"use client"

import React, {Fragment} from "react";
import {cn} from "@/lib/utils";
import {PortableText} from "@portabletext/react";
import {richTextComponent} from "@/sanity/sanityPortableText";
import {PortableTextBlock} from "sanity";
import Image from "next/image";
import {GROQImage} from "@/sanity/lib/types";
import {motion} from "framer-motion";

const fadeInStagger = (index: number, baseDelay = 0.1) => ({
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" as const },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: baseDelay + (index * 0.1) }
});

export default function NumberedIconList({
  items,
  className = '',
}: {
  items: {
    text: PortableTextBlock[];
    icon?: GROQImage;
  }[];
  className?: string;
}) {
  if (!items || items.length === 0) return null;

  return (
    <ul className={cn(
      "flex flex-col gap-[1rem]",
      className,
    )}>
      {items.map((item, index) => (
        <motion.li
          key={index}
          {...fadeInStagger(index)}
          className="flex items-start w-full min-h-[min(8rem,100%)] lg:min-h-[min(9rem,100%)] overflow-clip"
        >
          <p className="-translate-y-px translate-x-px mb-0 min-h-[6rem] w-[3.25rem] bg-[#D9F9B9] flex-shrink-0 [clip-path:polygon(0_0,100%_0,100%_100%,1.5rem_100%,0_calc(100%-1.5rem))] leading-none flex items-center justify-center font-geistmono font-medium text-black text-[2rem]">
            {index + 1}
          </p>

          <div className="bg-[#112628] -translate-x-[1px] grow flex items-center justify-between gap-[2rem] p-[1rem] border-l-[2px] border-l-[#D9F9B9]">
            <div className="richtext richtext-start prose-headings:text-white prose-headings:text-[2.5rem] prose-p:text-white/60 pl-[1.5rem] lg:pl-[2rem] py-[1rem]">
              <PortableText
                value={item?.text}
                components={richTextComponent}
              />
            </div>

            {item?.icon && (
              <div className="self-stretch flex-shrink-0 bg-[#D9F9B9]/10 w-[8rem] lg:w-[9rem] min-h-[8rem] lg:min-h-[9rem] flex items-center justify-center">
                <Image
                  className="!w-[5rem]"
                  src={item.icon || ""}
                  alt=""
                />
              </div>
            )}
          </div>
        </motion.li>
      ))}
    </ul>
  );
}